// A needsPlayer module's player picker (05c) — main.js initPlayerSearch, now React: an
// always-ready search box (just start typing — no picker to open first) over the hidden
// `.player-select`, which stays the real value + 'change' source (player-actions.ts).
//
// Two things are deliberately NOT React-controlled, to keep the DOM exactly main.js's:
//   - the select's ~1,100 <option>s are filled once from an HTML string (fork 2A): React
//     wouldn't write the `selected` attribute, and has nothing to diff this way;
//   - the input's text is set through a ref: a controlled input gets a `value` attribute.
//
// With ?live=1 (ids-3) the box searches the real players instead — all ~174,000, men's and
// women's: the nightly player file in the page (C2), else `/player-search`. See `liveBuild`
// below. The select stays the one source of the player either way.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { canvasApi } from "./shell-actions.js";
import { liveSearchLines, searchPlayerOptions, type LiveSearchRow, type PlayerOption, type SearchLine } from "./player-picker.js";
import { ensurePlayerOption, hasPlayerOption } from "./player-options.js";
import { isSnapshotPlayer, type PlayerId } from "../data/players.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { LIVE } from "../live/flag.js";
import { liveApi } from "../live/api.js";
import { rememberPickedPlayer, type PickedPlayer } from "../live/picked-players.js";
import { MIN_SEARCH_LETTERS, searchLivePlayers, searchRowText } from "../live/player-search.js";
import { indexSearchRows, loadedPlayerIndex, searchPlayerIndex, teamIdOfValue, teamValue, type TeamHit } from "../live/player-index.js";
import { moduleShowsTeams, teamDropRenderer, teamDropState } from "./team-modules.js";
import { setModState } from "../modules/bridge.js";
import { undoApi } from "./undo.js";
import { CountryFlag } from "../modules/CountryFlag.js";
import { teamOfModule } from "../live/team.js";
import { setStickyTeam } from "./sticky-team.js";

/** How long typing must pause before the live search asks — one call for "kerr", not one
 *  per letter (each takes ~3.5 s, and a late answer for "ker" must not win). */
const LIVE_SEARCH_PAUSE_MS = 300;

/** The same for the in-page search (C2): one search takes 55–90 ms over 174,000 names, so a
 *  fast typist (~8 keys a second) would feel each key. Short enough to still feel instant. */
const LOCAL_SEARCH_PAUSE_MS = 150;

interface Props {
  /** The `<option>` list, as module-shell's buildPlayerStrip wrote it. */
  optionsHtml: string;
  /** The module's player id (the store's, live since 05c). */
  player: PlayerId | null;
}

export function PlayerStrip({ optionsHtml, player }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const [lines, setLines] = useState<SearchLine[]>([]);
  const [open, setOpen] = useState(false);
  // Arrow keys and hover share one highlight; Enter picks it (or the top row).
  const [highlight, setHighlight] = useState(-1);
  // Live search only: the pause timer, a ticket per search (only the newest one's answer is
  // shown), and the last answer's players by id (a pick needs her name + team).
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTicket = useRef(0);
  const found = useRef(new Map<string, PickedPlayer>());
  // The teams the last search listed, by row value (a module that shows teams, e.g. the Bio).
  const foundTeams = useRef(new Map<string, TeamHit>());
  // An in-page search waiting out its pause (C2): Enter runs it at once, or "kane⏎" typed fast
  // would pick from the list still showing for "kan". Returns the new list's rows.
  const pendingLocal = useRef<(() => { value: string; text: string }[]) | null>(null);
  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTicket.current += 1; // an answer landing after unmount is dropped
    },
    [],
  );

  const selectEl = () => {
    if (!selectRef.current) throw new Error("PlayerStrip: .player-select missing");
    return selectRef.current;
  };
  const inputEl = () => {
    if (!inputRef.current) throw new Error("PlayerStrip: .player-search missing");
    return inputRef.current;
  };
  const currentLabel = () => {
    const select = selectEl();
    // A module showing a team names the team (teams and matches, 2026-10-30); its player stays
    // underneath, and picking one here brings the player back (player-actions.ts).
    const team = teamOfModule(select.closest(".placed-module"));
    if (team) return team.name;
    const opt = select.options[select.selectedIndex];
    return opt ? opt.text : "";
  };

  // StrictMode attaches refs twice; only the first fill counts.
  const fillSelect = useCallback(
    (el: HTMLSelectElement | null) => {
      selectRef.current = el;
      if (el && !el.firstChild) el.innerHTML = optionsHtml;
    },
    [optionsHtml],
  );

  // A module that shows teams says its box finds them too (read once the element is in the page).
  const [findsTeams, setFindsTeams] = useState(false);
  useLayoutEffect(() => {
    setFindsTeams(LIVE && moduleShowsTeams(selectEl().closest(".placed-module")));
  }, []);

  // The select's 'change' is the one way the player changes (see player-actions.ts).
  useLayoutEffect(() => {
    const select = selectEl();
    const mod = select.closest<HTMLElement>(".placed-module");
    if (!mod) throw new Error("PlayerStrip: not inside a .placed-module");
    const onChange = () => canvasApi().onPlayerSelectChange(mod, select);
    select.addEventListener("change", onChange);
    return () => select.removeEventListener("change", onChange);
  }, []);

  // Show the module's player — also when it changes from outside the box (a linked pick,
  // a comparison dropping back to one player, a drag-switch), or the box would show a
  // stale name while the module renders the right one.
  useLayoutEffect(() => {
    if (player !== null) selectEl().value = String(player);
    inputEl().value = currentLabel();
  }, [player]);

  useLayoutEffect(() => {
    if (highlight < 0) return;
    resultsRef.current?.querySelectorAll(".player-search-option")[highlight]?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const rows = lines.filter((l): l is Extract<SearchLine, { kind: "row" }> => l.kind === "row");

  function build(query: string): void {
    if (LIVE) {
      liveBuild(query);
      return;
    }
    const select = selectEl();
    const options: PlayerOption[] = Array.from(select.options, (o) => ({
      value: o.value,
      text: o.text,
      search: o.dataset.search,
    }));
    setLines(searchPlayerOptions(options, query, select.value));
    setHighlight(-1);
  }

  /**
   * The live search (fork 1B): everyone in the real DB, whether or not the snapshot has her —
   * a widget that isn't live yet says so for a player it can't show (bridge.tsx). One thing
   * still follows this picker's own list: a SNAPSHOT player it doesn't offer (an outfielder
   * in Goalkeeping) stays out, as on the snapshot site. A failed search says so in the list
   * and in the console; it never changes the module's player.
   *
   * C2: once the nightly player file is in (live/player-index.ts), the search runs in the page
   * at once, every match listed. Until then, if it failed, or when it finds nothing (a player
   * new since last night), the server search below answers, as before.
   */
  function liveBuild(query: string): void {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    pendingLocal.current = null;
    const ticket = ++searchTicket.current;
    const select = selectEl();
    const q = query.trim();
    const show = (lines: SearchLine[]) => {
      setLines(lines);
      setHighlight(-1);
    };
    if (q.length < MIN_SEARCH_LETTERS) {
      show(liveSearchLines({ kind: "short" }, select.value));
      return;
    }
    const offers = (p: PickedPlayer) => !isSnapshotPlayer(snapshotPlayersOrNone(), p.id) || hasPlayerOption(select, p.id);
    const askServer = () =>
      searchLivePlayers(liveApi(), q).then(
        (players) => {
          if (ticket !== searchTicket.current) return;
          const offered = players.filter(offers);
          found.current = new Map(offered.map((p) => [String(p.id), p]));
          const rows: LiveSearchRow[] = offered.map((p) => ({ value: String(p.id), text: searchRowText(p) }));
          show(liveSearchLines({ kind: "done", rows }, select.value));
        },
        (err: unknown) => {
          if (ticket !== searchTicket.current) return;
          console.error("[live] player search failed:", err);
          show(liveSearchLines({ kind: "failed" }, select.value));
        },
      );
    const index = loadedPlayerIndex();
    if (index) {
      const runLocal = () => {
        pendingLocal.current = null;
        const mod = select.closest(".placed-module");
        // Teams too, in a module that can show one (teams and matches, Ben 2026-10-30).
        const local = searchPlayerIndex(index, q, LIVE && moduleShowsTeams(mod));
        const teams = local.teams ?? [];
        foundTeams.current = new Map(teams.map((t) => [teamValue(t.id), t]));
        // The module's team, marked as its player is.
        const shownTeam = teamOfModule(mod);
        const current = shownTeam ? teamValue(shownTeam.id) : select.value;
        const players = local.players.filter(offers);
        const clubs = local.clubs.map((g) => ({ ...g, players: g.players.filter(offers) })).filter((g) => g.players.length > 0);
        if (players.length || clubs.length || teams.length) {
          found.current = new Map([...players, ...clubs.flatMap((g) => g.players)].map((p) => [String(p.id), p]));
          const lines = liveSearchLines({ kind: "done", rows: indexSearchRows({ players, clubs, teams }, searchRowText) }, current);
          show(lines);
          return lines.filter((l): l is Extract<SearchLine, { kind: "row" }> => l.kind === "row");
        }
        // Not in last night's file (a new player?) → the server, at once: the pause is behind us.
        show(liveSearchLines({ kind: "searching" }, select.value));
        void askServer();
        return [];
      };
      // The last list stays up while typing: no "Searching…" flash between keys.
      pendingLocal.current = runLocal;
      searchTimer.current = setTimeout(runLocal, LOCAL_SEARCH_PAUSE_MS);
      return;
    }
    show(liveSearchLines({ kind: "searching" }, select.value));
    searchTimer.current = setTimeout(() => void askServer(), LIVE_SEARCH_PAUSE_MS);
  }

  /** A team picked (a module that shows teams): the module shows it, one Undo step. Its player
   *  stays underneath; picking a player again brings them back (player-actions.ts). */
  function pickTeam(value: string): void {
    const t = foundTeams.current.get(value);
    if (!t) throw new Error(`PlayerStrip: picked ${value}, which the last search didn't return`);
    const mod = selectEl().closest<HTMLElement>(".placed-module");
    if (!mod) throw new Error("PlayerStrip: not inside a .placed-module");
    undoApi().saveState();
    // As a team dropped on it (team-modules.ts): a Bio's card first, a Matches' every game. The
    // newest season.
    const team = { id: t.id, name: t.name, competitionId: t.leagueId, season: null };
    // The next new team module opens on it (sticky-team.ts).
    setStickyTeam(team);
    for (const [k, v] of Object.entries(teamDropState(teamDropRenderer(mod), team))) setModState(mod, k, v);
    canvasApi().refreshModuleBody(mod);
    inputEl().value = t.name;
    setOpen(false);
    inputEl().blur();
  }

  function pick(value: string, text: string): void {
    if (teamIdOfValue(value) !== null) {
      pickTeam(value);
      return;
    }
    const select = selectEl();
    if (LIVE) {
      const p = found.current.get(value);
      if (!p) throw new Error(`PlayerStrip: picked ${value}, which the last search didn't return`);
      // Remembered first: the module's refresh below may need her name (bridge.tsx), and a
      // player the snapshot doesn't have needs an option before the select can hold her.
      rememberPickedPlayer(p);
      ensurePlayerOption(select, p);
    }
    // For the linked-players checkpoint: the handler needs the player this module had.
    select.dataset.previous = select.value;
    select.value = value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    // Live rows read "name — team"; the box shows her name, like the snapshot's.
    inputEl().value = LIVE ? currentLabel() : text;
    setOpen(false);
    inputEl().blur();
  }

  let rowIndex = -1;
  return (
    <>
      <div className="player-search-wrap">
        <input
          ref={inputRef}
          type="text"
          className="player-search"
          placeholder={findsTeams ? "Search player or team…" : "Search player…"}
          autoComplete="off"
          spellCheck={false}
          // (Select-all on click, incl. the Safari fix, is main.js's page-wide handler.)
          onFocus={() => {
            build("");
            setOpen(true);
          }}
          onInput={() => {
            build(inputEl().value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              inputEl().value = currentLabel();
              setOpen(false);
              inputEl().blur();
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (pendingLocal.current) {
                if (searchTimer.current) clearTimeout(searchTimer.current);
                const fresh = pendingLocal.current()[0];
                if (fresh) pick(fresh.value, fresh.text);
                return;
              }
              const entry = rows[highlight] ?? rows[0];
              if (entry) pick(entry.value, entry.text);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              if (rows.length) setHighlight(Math.min(highlight + 1, rows.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              if (rows.length) setHighlight(Math.max(highlight - 1, 0));
            }
          }}
          // Delayed so a pointerdown on a result commits before the blur puts the name back.
          onBlur={() => {
            setTimeout(() => {
              if (inputRef.current) inputRef.current.value = currentLabel();
              setOpen(false);
            }, 150);
          }}
        />
        {/* The rows stay after it closes (only `open` goes), as main.js left them. */}
        <div ref={resultsRef} className={open ? "player-search-results open" : "player-search-results"}>
          {lines.map((line, i) => {
            if (line.kind === "heading") {
              return (
                <div key={i} className="player-search-club">
                  {/* Flag + home league (Q-018): two clubs can share a name (Arsenal, England / Argentina). */}
                  <CountryFlag code={line.countryCode} />
                  {line.league ? `${line.text} · ${line.league}` : line.text}
                </div>
              );
            }
            if (line.kind === "label") {
              return (
                <div key={i} className="player-search-section">
                  {line.text}
                </div>
              );
            }
            if (line.kind === "hint") {
              return (
                <div key={i} className="picker-empty">
                  {line.text}
                </div>
              );
            }
            rowIndex += 1;
            const index = rowIndex;
            let className = line.team ? "player-search-option player-search-team" : "player-search-option";
            if (line.selected) className += " selected";
            if (index === highlight) className += " highlighted";
            return (
              <div
                key={i}
                className={className}
                onPointerDown={(e) => {
                  e.preventDefault();
                  pick(line.value, line.text);
                }}
                // No default highlight on the top row: it would look the same green as the
                // player already chosen. It only appears once you arrow-key or hover.
                onMouseEnter={() => setHighlight(index)}
              >
                {/* A team: its league's flag, the name, then the league ("🏴 Arsenal · Premier League"). */}
                {line.team ? (
                  <>
                    <CountryFlag code={line.team.countryCode} />
                    {line.text}
                    <span className="player-search-team-league"> · {line.team.league}</span>
                  </>
                ) : null}
                {/* Ben, 2026-10-23: "[nationality flag] Martin Ødegaard — Arsenal [club's country flag]".
                    A flag not known yet is left out (no placeholder). */}
                {!line.team && <CountryFlag code={line.nationality} />}
                {!line.team && line.text}
                {!line.team && line.clubCountry ? (
                  <span className="player-search-club-country">
                    <CountryFlag code={line.clubCountry} />
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      <select ref={fillSelect} className="module-controls player-select searchable" />
    </>
  );
}
